import { useCallback, useEffect, useRef, useState } from 'react';

const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

function pickMimeType() {
  return MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

/**
 * Enregistrement micro → Blob audio (MediaRecorder).
 * @param {{ maxDurationSeconds?: number | null }} options — si défini, arrêt automatique à cette durée (limite par déploiement / filiale).
 * @returns {{ phase: string, blob: Blob | null, error: string | null, hitMaxDuration: boolean, start: () => Promise<void>, stop: () => void, reset: () => void }}
 */
export function useAudioRecorder(options = {}) {
  const { maxDurationSeconds } = options;

  const [phase, setPhase] = useState('idle');
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState(null);
  const [hitMaxDuration, setHitMaxDuration] = useState(false);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const chosenMimeRef = useRef('');
  const recordingStartedAtRef = useRef(null);

  const reset = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
    chosenMimeRef.current = '';
    recordingStartedAtRef.current = null;
    setBlob(null);
    setError(null);
    setHitMaxDuration(false);
    setPhase('idle');
  }, []);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state === 'recording') {
      setPhase('finalizing');
      rec.stop();
    }
  }, []);

  const start = useCallback(async () => {
    reset();
    chosenMimeRef.current = pickMimeType();

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError('micDenied');
      setPhase('error');
      return;
    }

    streamRef.current = stream;
    const optionsRec = chosenMimeRef.current ? { mimeType: chosenMimeRef.current } : undefined;
    const rec = new MediaRecorder(stream, optionsRec);
    recorderRef.current = rec;

    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    rec.onstop = () => {
      const type = chosenMimeRef.current || 'audio/webm';
      const nextBlob = new Blob(chunksRef.current, { type });
      setBlob(nextBlob);
      setPhase('ready');
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };

    recordingStartedAtRef.current = Date.now();
    rec.start();
    setPhase('recording');
  }, [reset]);

  useEffect(() => {
    if (phase !== 'recording' || maxDurationSeconds == null || maxDurationSeconds <= 0) {
      return undefined;
    }
    const maxMs = maxDurationSeconds * 1000;
    const id = setInterval(() => {
      const started = recordingStartedAtRef.current;
      if (!started) return;
      if (Date.now() - started >= maxMs) {
        setHitMaxDuration(true);
        const rec = recorderRef.current;
        if (rec && rec.state === 'recording') {
          setPhase('finalizing');
          rec.stop();
        }
      }
    }, 250);
    return () => clearInterval(id);
  }, [phase, maxDurationSeconds]);

  return { phase, blob, error, hitMaxDuration, start, stop, reset };
}
